import ReactMarkdown from "react-markdown";
import LinkifyText from "../LinkifyText";
import TrainingPlanViewer from "../TrainingPlanViewer";
import { Target, Video, Dumbbell, CheckCircle, Brain, Calendar, Lightbulb, Star } from "lucide-react";

// Helper: render text with exercise links
function renderTextWithExerciseLinks(text) {
  const linkPattern = /Link:\s*(?:category=([a-z-]+)|(?:\[)?([^(\n\]]+?)(?:\]|\(|$))/gi;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = linkPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    const category = match[1];
    const exerciseName = match[2];

    if (category) {
      const categoryUrl = `/training?category=${encodeURIComponent(category)}`;
      const categoryDisplay = category.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      parts.push(
        <a key={`link-${match.index}`} href={categoryUrl}
          className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium underline decoration-emerald-300 hover:decoration-emerald-500 transition-colors bg-emerald-50 px-2 py-1 rounded"
          title={`View ${categoryDisplay} exercises on Mental Training page`}>
          <Target className="w-3 h-3" />{categoryDisplay} Exercises
        </a>
      );
    } else if (exerciseName && exerciseName.trim()) {
      const exerciseUrl = `/training?exercise=${encodeURIComponent(exerciseName.trim())}`;
      parts.push(
        <a key={`link-${match.index}`} href={exerciseUrl}
          className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-medium underline decoration-emerald-300 hover:decoration-emerald-500 transition-colors bg-emerald-50 px-2 py-1 rounded"
          title={`Go to ${exerciseName.trim()} on Mental Training page`}>
          <Target className="w-3 h-3" />{exerciseName.trim()}
        </a>
      );
    }
    lastIndex = linkPattern.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : <LinkifyText>{text}</LinkifyText>;
}

// Labels that should NOT be turned into exercise links
const SKIP_LABELS = ['what to do', 'why this works', 'tips', 'week overview', 'goals for this week', 'week notes', 'week tips', 'program overview', 'success tips', 'weekly reflection', 'weekly reflection & recovery', 'key exercises'];
const DAY_NAMES_LOWER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function isExerciseName(text) {
  if (!text || typeof text !== 'string') return false;
  const lower = text.toLowerCase().replace(/[:\*]/g, '').trim();
  if (SKIP_LABELS.includes(lower)) return false;
  if (DAY_NAMES_LOWER.includes(lower)) return false;
  if (lower.length < 4) return false;
  if (text.trim().endsWith(':')) return false;
  if (['focus', 'notes', 'overview', 'goals', 'summary'].includes(lower)) return false;
  return true;
}

export function renderDayContent(contentLines) {
  // Join preserving blank lines between sections (critical for markdown parsing)
  const content = contentLines.join('\n');

  // Pre-process: ensure "What to do:", "Why this works:", and "Tips:" start on their own line
  const enhanced = content
    // Convert inline "Why it works:" into its own paragraph
    .replace(/(?<=\.\s)Why it works:\s*/gi, '\n\n**Why this works:** ')
    .replace(/(?<=\.\s)Tip:\s*/gi, '\n\n**Tips:** ')
    // Ensure **Tips:** always starts on a new line with a blank line before it
    .replace(/([^\n])\n(\s*\*\*Tips?:?\*\*)/gi, '$1\n\n$2')
    // Ensure **Why this works:** always starts on a new line with a blank line before it
    .replace(/([^\n])\n(\s*\*\*Why (?:this|it) works:?\*\*)/gi, '$1\n\n$2')
    // Ensure **What to do:** always starts on a new line with a blank line before it
    .replace(/([^\n])\n(\s*\*\*What to do:?\*\*)/gi, '$1\n\n$2')
    // Ensure tip bullets after **Tips:** have proper spacing
    .replace(/(\*\*Tips?:?\*\*)\s*\n\s*-/gi, '$1\n\n-');

  return (
    <div className="prose prose-sm max-w-none">
      <ReactMarkdown
        components={{
          h1: ({ children }) => <h1 className="text-xl font-bold mt-4 mb-2 text-gray-900">{children}</h1>,
          h2: ({ children }) => <h2 className="text-lg font-bold mt-4 mb-2 text-gray-800">{children}</h2>,
          h3: ({ children }) => <h3 className="text-base font-bold mt-3 mb-1.5 text-gray-800">{children}</h3>,
          h4: ({ children }) => <h4 className="text-sm font-bold mt-2 mb-1 text-gray-700">{children}</h4>,
          p: ({ children }) => <p className="text-sm text-gray-700 leading-relaxed mb-2">{children}</p>,
          strong: ({ children }) => {
            const text = typeof children === 'string' ? children : (Array.isArray(children) ? children.join('') : String(children || ''));
            const cleanText = text.replace(/[:\*]/g, '').trim();

            // Style "What to do" section header
            if (/^What to do/i.test(cleanText)) {
              return (
                <span className="flex items-center gap-1.5 text-blue-700 font-bold text-sm mt-3 mb-1">
                  <Dumbbell className="w-3.5 h-3.5 text-blue-500" />
                  {text}
                </span>
              );
            }

            // Style "Why this works" section header
            if (/^Why (this|it) works/i.test(cleanText)) {
              return (
                <span className="flex items-center gap-1.5 text-purple-700 font-bold text-sm mt-3 mb-1">
                  <Lightbulb className="w-3.5 h-3.5 text-purple-500" />
                  {text}
                </span>
              );
            }

            // Style "Tips" section header — render as block element for visual separation
            if (/^Tips?$/i.test(cleanText) || /^Tips?:/i.test(text)) {
              return (
                <span className="flex items-center gap-1.5 text-amber-700 font-bold text-sm mt-3 mb-1">
                  <Star className="w-3.5 h-3.5 text-amber-500" />
                  {text}
                </span>
              );
            }

            if (isExerciseName(text)) {
              const encodedName = encodeURIComponent(cleanText);
              return (
                <span className="inline-flex items-center gap-1 flex-wrap">
                  <strong className="font-semibold text-gray-900">{text}</strong>
                  <a href={`/training?exercise=${encodedName}`}
                    className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-1.5 py-0.5 rounded-full transition-colors no-underline"
                    title={`Practice ${cleanText} on Exercises page`}
                    onClick={(e) => e.stopPropagation()}>
                    <Dumbbell className="w-2.5 h-2.5" />Exercise
                  </a>
                </span>
              );
            }

            return <strong className="font-semibold text-gray-900">{children}</strong>;
          },
          ul: ({ children }) => <ul className="list-disc pl-5 mb-3 space-y-1.5">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-5 mb-3 space-y-1.5">{children}</ol>,
          li: ({ children }) => <li className="text-sm text-gray-700 leading-relaxed">{children}</li>,
          blockquote: ({ children }) => <blockquote className="border-l-3 border-emerald-400 pl-3 italic text-gray-600 my-3">{children}</blockquote>,
          a: ({ href, children }) => <a href={href} className="text-emerald-600 hover:text-emerald-700 underline" target="_blank" rel="noopener noreferrer">{children}</a>,
        }}
      >
        {enhanced}
      </ReactMarkdown>
    </div>
  );
}

export function parseWeeklyPlan(planContent) {
  if (!planContent) return [];

  const weeks = [];
  const weekMap = new Map();
  const lines = planContent.split('\n');
  let currentWeek = null;
  let currentDay = null;
  let dayNumber = 1;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Only match "Week X" as a header: line must START with optional markdown heading/bold markers
    // followed by "Week X". This avoids matching inline references like "...from Week 1, these..."
    const trimmed = line.trim();
    const weekMatch = trimmed.match(/^(?:#{1,4}\s+|\*{1,3}\s*)?Week\s*(\d+)[\s\:\-\*#]*/i);
    const isWeekHeader = weekMatch && trimmed.length < 100;
    if (isWeekHeader) {
      const weekNum = parseInt(weekMatch[1]);

      if (currentDay && currentWeek) {
        currentWeek.days.push(currentDay);
        currentDay = null;
      }

      if (currentWeek) {
        weekMap.set(currentWeek.number, currentWeek);
      }

      if (!weekMap.has(weekNum)) {
        currentWeek = { number: weekNum, days: [], content: [] };
        dayNumber = (weekNum - 1) * 7 + 1;
      } else {
        currentWeek = weekMap.get(weekNum);
        const lastDayInWeek = currentWeek.days.length > 0
          ? currentWeek.days[currentWeek.days.length - 1].number
          : dayNumber - 1;
        dayNumber = lastDayInWeek + 1;
      }
      continue;
    }

    // Skip patterns like "Monday to Sunday" or "Monday - Sunday" (these are range headers, not day headers)
    const isDayRange = /(?:Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s*(?:to|through|-|–)\s*(?:Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)/i.test(line);
    
    // Match individual day headers: **Monday:**, Day 1:, etc.
    const dayMatch = !isDayRange && line.match(/\*{0,3}\s*(?:Day\s+(\d+)|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s*\*{0,3}\s*:?/i);
    const isDayHeader = dayMatch && line.trim().length < 80 && !line.toLowerCase().includes('week overview');

    if (isDayHeader && currentWeek) {
      if (currentDay) {
        currentWeek.days.push(currentDay);
      }

      let dayOfWeek = dayMatch[1] ? parseInt(dayMatch[1]) : null;
      if (!dayOfWeek) {
        const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        const dayName = line.toLowerCase();
        for (let j = 0; j < dayNames.length; j++) {
          if (dayName.includes(dayNames[j])) {
            dayOfWeek = j + 1;
            break;
          }
        }
      }

      currentDay = {
        number: dayNumber++,
        dayOfWeek: dayOfWeek || dayNumber,
        content: [],
        dayName: line.trim().replace(/\*\*/g, '').replace(/:$/, '')
      };
      continue;
    }

    if (line.trim()) {
      if (currentDay) {
        currentDay.content.push(line);
      } else if (currentWeek) {
        currentWeek.content.push(line);
      }
    }
  }

  if (currentDay && currentWeek) {
    currentWeek.days.push(currentDay);
  }
  if (currentWeek) {
    weekMap.set(currentWeek.number, currentWeek);
  }

  const sortedWeeks = Array.from(weekMap.values()).sort((a, b) => a.number - b.number);

  const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  sortedWeeks.forEach(week => {
    if (!week.content) week.content = [];
    const weekBase = (week.number - 1) * 7;

    // Fallback: if no days were parsed, split week content evenly into 7 days
    if (week.days.length === 0 && week.content.length > 0) {
      const contentPerDay = Math.ceil(week.content.length / 7);
      for (let i = 0; i < 7; i++) {
        const start = i * contentPerDay;
        const end = Math.min((i + 1) * contentPerDay, week.content.length);
        const dayContent = week.content.slice(start, end);
        if (dayContent.length > 0) {
          week.days.push({
            number: weekBase + i + 1,
            dayOfWeek: i + 1,
            content: dayContent,
            dayName: DAY_NAMES[i]
          });
        }
      }
    }

    // Hard-cap at 7 days per week and normalize numbering
    week.days = week.days.slice(0, 7);
    week.days.forEach((day, idx) => {
      day.number = weekBase + idx + 1;
      day.dayOfWeek = idx + 1;
      day.dayName = DAY_NAMES[idx];
    });

    // Fill missing days up to 7 so every week always shows Mon-Sun
    while (week.days.length < 7) {
      const idx = week.days.length;
      week.days.push({
        number: weekBase + idx + 1,
        dayOfWeek: idx + 1,
        content: [],
        dayName: DAY_NAMES[idx]
      });
    }
  });

  return sortedWeeks;
}

export function EnhancedPlanRenderer({ plan, activeWeek, onMarkDayComplete }) {
  const weeks = parseWeeklyPlan(plan.plan_content);
  const completedDays = plan.completed_days || [];

  if (weeks.length === 0) {
    return (
      <div className="prose max-w-none">
        <TrainingPlanViewer planContent={plan.plan_content} />
      </div>
    );
  }

  const week = weeks.find(w => w.number === activeWeek);
  if (!week) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Brain className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="text-sm">No content available for Week {activeWeek} yet.</p>
        <p className="text-xs mt-1">This week's plan may still be generating.</p>
      </div>
    );
  }

  const completedDaysInWeek = week.days.filter(day => completedDays.includes(day.number)).length;
  const weekProgress = (completedDaysInWeek / 7) * 100;
  const isWeekComplete = completedDaysInWeek === 7;

  return (
    <div className="space-y-4">
      {/* Week Header */}
      <div
        id={`week-${week.number}`}
        className={`rounded-xl overflow-hidden ${
          isWeekComplete
            ? 'border-2 border-emerald-500 ring-2 ring-emerald-200'
            : 'border border-emerald-100'
        }`}
      >
        <div className={`px-6 py-4 ${
          isWeekComplete
            ? 'bg-gradient-to-r from-emerald-600 to-teal-600'
            : 'bg-gradient-to-r from-emerald-500 to-teal-500'
        } text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-left flex-1">
              {isWeekComplete ? (
                <CheckCircle className="w-7 h-7 flex-shrink-0 animate-pulse" />
              ) : (
                <Brain className="w-6 h-6 flex-shrink-0" />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-bold">Week {week.number}</h3>
                  {isWeekComplete && (
                    <span className="px-2 py-1 bg-white/30 text-xs rounded-full font-semibold">COMPLETED</span>
                  )}
                </div>
                {!isWeekComplete && weekProgress > 0 && (
                  <div className="mt-2 w-full bg-white/20 rounded-full h-1.5 overflow-hidden">
                    <div className="h-full bg-white transition-all duration-500" style={{ width: `${weekProgress}%` }} />
                  </div>
                )}
              </div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span className="text-sm font-medium">{completedDaysInWeek}/7 days</span>
            </div>
          </div>
        </div>

        {/* Week Overview */}
        <div className="bg-white p-6 space-y-4">
          {week.content && week.content.length > 0 && (() => {
            // Separate "Goals for This Week" into its own section below overview
            const goalsIdx = week.content.findIndex(l => /goals?\s+for\s+this\s+week/i.test(l));
            const overviewLines = goalsIdx >= 0 ? week.content.slice(0, goalsIdx) : week.content;
            const goalsLines = goalsIdx >= 0 ? week.content.slice(goalsIdx) : [];

            return (
              <>
                {overviewLines.length > 0 && (
                  <div className="bg-gradient-to-r from-teal-50 to-emerald-50 border-l-4 border-teal-500 p-5 rounded-r-lg mb-4">
                    <h4 className="text-lg font-bold text-teal-900 mb-3 flex items-center gap-2">
                      <Target className="w-6 h-6" />
                      Week {week.number} - Overview & Objectives
                    </h4>
                    <div className="text-sm text-gray-800 leading-relaxed space-y-2">
                      {renderDayContent(overviewLines)}
                    </div>
                  </div>
                )}
                {goalsLines.length > 0 && (
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-l-4 border-amber-500 p-5 rounded-r-lg mb-4">
                    <h4 className="text-lg font-bold text-amber-900 mb-3 flex items-center gap-2">
                      <Star className="w-6 h-6" />
                      Goals for This Week
                    </h4>
                    <div className="text-sm text-gray-800 leading-relaxed space-y-2">
                      {renderDayContent(goalsLines)}
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          {/* Days */}
          <div className="space-y-3">
            {week.days.map((day) => {
              const isCompleted = completedDays.includes(day.number);
              return (
                <div
                  key={day.number}
                  data-testid={`day-${day.dayName?.toLowerCase() || day.number}`}
                  className={`rounded-lg border-2 transition-all duration-200 ${
                    isCompleted
                      ? 'border-emerald-400 bg-emerald-50'
                      : 'border-gray-200 bg-white hover:border-emerald-200'
                  }`}
                >
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex flex-col items-center gap-1">
                        <button
                          data-testid={`complete-day-${day.number}`}
                          onClick={() => onMarkDayComplete(plan.id, day.number, !isCompleted)}
                          className={`flex-shrink-0 w-8 h-8 rounded-lg border-2 flex items-center justify-center transition-all duration-200 shadow-sm ${
                            isCompleted
                              ? 'bg-emerald-500 border-emerald-500 shadow-emerald-200'
                              : 'bg-white border-gray-400 hover:border-emerald-500 hover:shadow-md'
                          }`}
                          title={isCompleted ? "Mark as incomplete" : "Mark as complete"}
                        >
                          {isCompleted && <CheckCircle className="w-5 h-5 text-white" />}
                        </button>
                        <span className="text-[10px] text-gray-500 font-medium">
                          {isCompleted ? 'Done' : 'To Do'}
                        </span>
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className={`text-lg font-bold ${isCompleted ? 'text-emerald-700' : 'text-gray-800'}`}>
                            {day.dayName || `Day ${day.dayOfWeek}`}
                            {isCompleted && (
                              <span className="ml-2 px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded-full font-semibold">Completed</span>
                            )}
                          </h4>
                        </div>

                        <div className={`bg-white rounded-lg p-4 border border-gray-200 ${isCompleted ? 'opacity-75' : ''}`}>
                          <div className="space-y-1">
                            {day.content && day.content.length > 0 ? (
                              renderDayContent(day.content)
                            ) : (
                              <div className="text-center py-8 text-gray-500">
                                <p className="text-sm">No specific activities listed for this day.</p>
                                <p className="text-xs mt-2">Check the week overview above for general guidance.</p>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 p-3 bg-teal-50 border-l-4 border-teal-400 rounded">
                          <p className="text-xs text-teal-800 font-medium">
                            <strong>Tip:</strong> Take your time with each exercise. Quality over quantity.
                            {!isCompleted && " Check the box above when you complete today's activities."}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
